'use client';

import React from 'react';

import jsCookie from 'js-cookie';
import { useRouter, useSearchParams } from 'next/navigation';

import { LoginForm } from '@/components/V2/Auth';
import { getClientEnv } from '@/config/clientEnv';
import ToastWrapper from '@/layouts/ToastWrapper';
import { useAppDispatch } from '@/store';
import { customerApi } from '@/store/api/customer';
import { CustomerType } from '@/types/CustomerType';

const BO_THIRD_PARTY_SIGNUP_COOKIE = '__bo_third_party_signup';

const BackofficeLoginContainer = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [thirdPartyLoginHandler, setThirdPartyLoginHandler] = React.useState<
    ((provider: number, token: string) => Promise<void>) | null
  >(null);

  const returnUrl = searchParams.get('ReturnUrl') || undefined;

  const handleAuthSuccess = (loginData: {
    rememberMe: boolean;
    returnUrl?: string | null;
    shouldShow2FA?: boolean;
    shouldEnter2FA?: boolean;
    customerTypeID?: number;
  }) => {
    // If user is not a Pet Pro, redirect to RS homepage (logged-in state)
    if (loginData.customerTypeID !== undefined && loginData.customerTypeID !== CustomerType.PET_PRO) {
      const { REPLICATED_URL } = getClientEnv();
      window.location.href = REPLICATED_URL || '/';
      return;
    }

    // If is2FAEnabled, do NOT redirect - show modal on login page
    if (loginData.shouldEnter2FA) {
      // Store the intended redirect URL in localStorage for after 2FA verification
      const intendedUrl = loginData.returnUrl || returnUrl || '/';
      try {
        localStorage.setItem('__2fa_redirect_url', intendedUrl);
      } catch (e) {
        console.error('Failed to store redirect URL:', e);
      }

      // Add modal param to current URL to trigger modal
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('modal', 'enter2FA');
      router.push(currentUrl.pathname + currentUrl.search);
      dispatch(customerApi.util.invalidateTags(['Customers']));
      return;
    }

    // For modal=enable2FA only (first time users), redirect with flag
    const addFlags = (base: string) => {
      let url = base;
      if (loginData.shouldShow2FA) {
        url = url.includes('?') ? `${url}&modal=enable2FA` : `${url}?modal=enable2FA`;
      }
      return url;
    };

    if (loginData.returnUrl) {
      router.push(addFlags(loginData.returnUrl));
      return;
    }

    // Redirect to homepage or returnUrl with 2FA flags
    if (returnUrl) {
      const decoded = decodeURIComponent(returnUrl);
      router.push(addFlags(decoded));
    } else {
      router.push(addFlags('/'));
    }

    dispatch(customerApi.util.invalidateTags(['Customers']));
  };

  const handleThirdPartyNewUser = (data: {
    token: string;
    type: string;
    email: string;
    firstName: string;
    lastName: string;
    picture: string;
  }) => {
    try {
      const rootDomain = `.${window.location.host.split('.').slice(-2).join('.')}`;
      jsCookie.set(BO_THIRD_PARTY_SIGNUP_COOKIE, JSON.stringify(data), {
        domain: rootDomain,
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        expires: 1 / 24,
      });
    } catch (e) {
      console.error('Failed to set third-party signup cookie:', e);
    }
    window.location.href = `${getClientEnv().REPLICATED_URL}/pawtree/account/login?thirdPartySignup=true`;
  };

  return (
    <div className="v2-fixed v2-inset-0 v2-flex v2-items-center v2-justify-center v2-p-4 backoffice-login-background">
      <div className="v2-bg-white v2-p-8 v2-shadow-xl v2-rounded-xl v2-max-w-[640px] v2-w-full">
        <LoginForm
          onSubmit={handleAuthSuccess}
          isBackoffice={true}
          onThirdPartyLogin={handler => setThirdPartyLoginHandler(() => handler)}
          onThirdPartyNewUser={handleThirdPartyNewUser}
        />
        <ToastWrapper />
      </div>
    </div>
  );
};

export default BackofficeLoginContainer;
